import { Dialog } from '@capacitor/dialog'
import { NativeBiometric } from '@capgo/capacitor-native-biometric'
import styled from '@emotion/styled'
import { defineMessages, FormattedMessage, useIntl } from '@faceup/localization'
import { Typography } from '@faceup/ui-base'
import { useContext, useEffect, useRef, useState } from 'react'
import { UserContext } from '../../Contexts/UserContext'
import ShieldIcon from './assets/shield-icon.svg?react'

const { Title } = Typography

const messages = defineMessages('followUp.pages.authentication.userLogin', [
  { id: 'signUpTitle', defaultMessage: '' },
  { id: 'signUpDescription', defaultMessage: '' },
  { id: 'confirmTitle', defaultMessage: '' },
  { id: 'confirmDescription', defaultMessage: '' },
  { id: 'loginTitle', defaultMessage: '' },
  { id: 'continue', defaultMessage: '' },
  { id: 'pinDiffers', defaultMessage: '' },
  { id: 'resetApp', defaultMessage: '' },
  { id: 'forgottenPin', defaultMessage: '' },
  { id: 'forgottenPinTitle', defaultMessage: '' },
  { id: 'forgottenPinDecription', defaultMessage: '' },
] as const)

type LoginType = 'signUp' | 'confirm' | 'login'

const Welcome = () => {
  const {
    login: { authenticate, biometricsAuthentication, hasAccount, signUp },
    logout,
    settings,
  } = useContext(UserContext)
  const [loginType, _setLoginType] = useState<LoginType>(hasAccount ? 'login' : 'signUp')
  const [pin, _setPin] = useState('')
  const [pinError, _setPinError] = useState(false)
  const [temporaryPin, _setTemporaryPin] = useState('')
  const pinRef = useRef(pin)
  const loginTypeRef = useRef(loginType)
  const temporaryPinRef = useRef(temporaryPin)
  const pinErrorRef = useRef(pinError)
  const { formatMessage } = useIntl()

  // Because of using states in event listeners
  const setPin = (value: string) => {
    pinRef.current = value
    _setPin(value)
    if (isFormValid()) {
      setTimeout(() => handleButton(), 300)
    }
  }

  const setTemporaryPin = (value: string) => {
    temporaryPinRef.current = value
    _setTemporaryPin(value)
  }

  const setLoginType = (loginType: LoginType) => {
    loginTypeRef.current = loginType
    _setLoginType(loginType)
  }

  const setPinError = (value: boolean) => {
    pinErrorRef.current = value
    _setPinError(value)
  }

  const hiddenInputRef = useRef<HTMLInputElement>(null)
  const isFormValid = () => pinRef.current.length === 4

  const handleButton = async () => {
    switch (loginTypeRef.current) {
      case 'signUp':
        setTemporaryPin(pinRef.current)
        setLoginType('confirm')
        setPin('')
        return
      case 'confirm':
        if (pinRef.current !== temporaryPinRef.current) {
          setPinError(true)
          setPin('')
          return
        }

        signUp(pinRef.current)
        return
      default:
        if (!(await authenticate(pinRef.current))) {
          setPinError(true)
          setPin('')
          return
        }
    }
  }

  const handleLogout = async () => {
    const { value } = await Dialog.confirm({
      title: formatMessage(messages.forgottenPinTitle),
      message: formatMessage(messages.forgottenPinDecription),
    })

    if (value) {
      logout()
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only run on mount to set up event listeners once
  useEffect(() => {
    const focusInput = () => hiddenInputRef?.current?.focus()

    const biometrics = async () => {
      try {
        const config = await NativeBiometric.isAvailable()

        if (config.isAvailable) {
          await NativeBiometric.verifyIdentity()
          // successfuly authenticates
          setTimeout(biometricsAuthentication, 500)
        }
      } catch {
        setTimeout(focusInput, 300)
      }
    }

    document.body.addEventListener('click', focusInput)

    if (settings?.biometrics) {
      biometrics()
    } else {
      focusInput()
    }

    return () => {
      document.body.removeEventListener('click', focusInput)
    }
  }, [])

  return (
    <>
      <div className='text-center pt-[15%] w-full'>
        <span className='inline-flex text-center'>
          <ShieldIcon className='text-[100px] mb-8' />
        </span>
        <Title
          style={{
            fontSize: 24,
            marginBottom: loginType === 'login' ? 42 : 12,
          }}
        >
          <FormattedMessage message={messages[`${loginType}Title`]} />
        </Title>
        {loginType !== 'login' && (
          <div className='text-[1.2rem] mb-8'>
            <FormattedMessage message={messages[`${loginType}Description`]} />
          </div>
        )}
        <div className='flex flex-nowrap items-center justify-center'>
          <HiddenInput
            ref={hiddenInputRef}
            pattern='[0-9]*'
            type='number'
            value={pin}
            onChange={({ target: { value } }) => {
              if (value.length <= 4) {
                setPin(value)
              }
            }}
          />
          {Array.from({ length: 4 }).map((_, i) => (
            <InputWrapper
              // biome-ignore lint/suspicious/noArrayIndexKey: Static array of 4 PIN input dots
              key={i}
              filled={pin.length > i}
            >
              <InputCode filled={pin.length > i} />
            </InputWrapper>
          ))}
        </div>
        {pinError && (
          <div className='text-[1.2rem] mt-6 text-[#ef4a45]'>
            <FormattedMessage message={messages.pinDiffers} />
          </div>
        )}
      </div>
      {hasAccount && (
        <div className='text-[#0e9af7] font-semibold text-4' onClick={handleLogout}>
          <FormattedMessage message={messages.forgottenPin} />
        </div>
      )}
    </>
  )
}

const HiddenInput = styled.input`
  height: 0;
  width: 0;
  opacity: 0;
  /* Firefox */
  &[type='number'] {
    -moz-appearance: textfield;
  }
`
const InputCode = styled.div<{ filled: boolean }>`
  border: none;
  border-radius: 50%;
  width: 1.4rem;
  height: 1.4rem;
  margin: 4px;

  background-color: ${({ filled }) => (filled ? '#0e9af7' : 'transparent')};

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`

const InputWrapper = styled.div<{ filled: boolean }>`
  border: 2px solid #0e9af7;
  border-radius: 50%;
   margin: 0.5rem;

  border-color: ${({ filled }) => (filled ? '#0e9af7' : '#d7dde1')};
`

export default Welcome
